/**
 * FileFeaturesPage
 * File-centric mapping tool: enter a code-file path (or glob) + repo, see which
 * product features currently map to it, then add features or replace the whole
 * set. Mirrors the MCP tools get_features_for_file / set_features_for_file.
 *
 * The file does NOT need to physically exist — this only registers the mapping
 * in feature_code_files, so it works for both existing and brand-new files.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

function IconPlus() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function IconX() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

export default function FileFeaturesPage() {
  // ?repo=&file_path= prefills the form — used by the dashboard coverage drill-down.
  const [searchParams] = useSearchParams();
  const [repo, setRepo]           = useState(() => searchParams.get('repo') || '');
  const [filePath, setFilePath]   = useState(() => searchParams.get('file_path') || '');

  const [current, setCurrent]     = useState(null);   // features currently mapped
  const [lookupDone, setLookupDone] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  // Feature picker
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected]   = useState([]);     // [{feature_id, major_feature, sub_feature, product_name}]
  const debounceRef = useRef(null);

  const [replace, setReplace]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [result, setResult]       = useState(null);

  const canQuery = repo.trim() && filePath.trim();

  // ── Look up current mappings ────────────────────────────────────────────────
  const lookup = useCallback(async () => {
    if (!canQuery) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.get('/code-files/for-file', {
        params: { file_path: filePath.trim(), repo: repo.trim() },
      });
      setCurrent(res.data.features || []);
      setLookupDone(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Lookup failed');
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [canQuery, filePath, repo]);

  // Run the lookup straight away when both values arrived in the URL.
  const autoLookupDone = useRef(false);
  useEffect(() => {
    if (autoLookupDone.current) return;
    if (searchParams.get('repo') && searchParams.get('file_path')) {
      autoLookupDone.current = true;
      lookup();
    }
  }, [searchParams, lookup]);

  // ── Feature search ──────────────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get('/features', { params: { q, limit: 20 } });
        setResults(res.data.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  function addSelected(f) {
    if (selected.some(s => s.feature_id === f.feature_id)) return;
    setSelected(prev => [...prev, f]);
  }

  function removeSelected(fid) {
    setSelected(prev => prev.filter(s => s.feature_id !== fid));
  }

  // ── Save mapping ────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!canQuery || selected.length === 0) return;
    setSaving(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post('/code-files/map', {
        file_path: filePath.trim(),
        repo_name: repo.trim(),
        feature_ids: selected.map(s => s.feature_id),
        replace,
      });
      setResult(res.data);
      setSelected([]);
      setQuery('');
      setResults([]);
      // Refresh the current-mapping view to reflect the change
      setCurrent(res.data.features.map(f => ({
        feature_id: f.feature_id,
        major_feature: f.major_feature,
      })));
      setLookupDone(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h2>Map File → Features</h2>
        <p>
          Link a code file (existing or new) to the product features it implements.
          This registers the mapping used by blast-radius and code review.
        </p>
      </div>

      <div className="page-body">
        {/* File + repo inputs */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="form-row" style={{ marginBottom: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Repository</label>
              <input
                className="form-input"
                placeholder="voice  (name or slug)"
                value={repo}
                onChange={e => { setRepo(e.target.value); setLookupDone(false); }}
                onKeyDown={e => e.key === 'Enter' && lookup()}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">File path or glob</label>
              <input
                className="form-input"
                style={{ fontFamily: 'monospace' }}
                placeholder="src/voice/ivr_handler.py"
                value={filePath}
                onChange={e => { setFilePath(e.target.value); setLookupDone(false); }}
                onKeyDown={e => e.key === 'Enter' && lookup()}
              />
            </div>
          </div>
          <button className="btn btn-secondary" style={{ fontSize: 12 }}
            disabled={!canQuery || loading} onClick={lookup}>
            {loading ? 'Looking up…' : 'Look up current features'}
          </button>
        </div>

        {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

        {result && (
          <div className="card" style={{ marginBottom: 16, borderColor: 'var(--success)' }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--success)' }}>
              ✓ Mapping saved
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Added {result.added} · Already mapped {result.skipped_existing}
              {result.removed > 0 && <> · Unlinked {result.removed}</>}
            </div>
            {result.not_found?.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
                Unknown feature IDs skipped: {result.not_found.join(', ')}
              </div>
            )}
            {result.repository_id === null && (
              <div style={{ fontSize: 12, color: 'var(--warning, #d97706)', marginTop: 6 }}>
                ⚠ Repo name didn’t resolve to a known repository — coverage stats
                won’t update for it.
              </div>
            )}
          </div>
        )}

        {/* Current mappings */}
        {lookupDone && current && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
              Currently mapped features ({current.length})
            </h3>
            {current.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                No features map to this file yet — add some below.
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {current.map(f => (
                  <span key={f.feature_id} style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 999,
                    background: 'var(--surface-hover, #1e293b)',
                    border: '1px solid var(--border)', color: 'var(--text-primary)',
                  }}>
                    <code style={{ color: 'var(--info)' }}>{f.feature_id}</code>
                    {f.major_feature ? ` · ${f.major_feature}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Feature picker + save */}
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
            Add features to this file
          </h3>

          {/* Selected chips */}
          {selected.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {selected.map(f => (
                <span key={f.feature_id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, padding: '4px 6px 4px 10px', borderRadius: 999,
                  background: 'var(--info)', color: '#fff', fontWeight: 500,
                }}>
                  {f.feature_id}
                  <button onClick={() => removeSelected(f.feature_id)}
                    title="Remove"
                    style={{ background: 'transparent', border: 'none', color: '#fff',
                      cursor: 'pointer', display: 'flex', padding: 0 }}>
                    <IconX />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Search box */}
          <input
            className="form-input"
            style={{ marginBottom: 8 }}
            placeholder="Search features by name or description…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />

          {/* Search results */}
          {query.trim() && (
            <div style={{
              border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              maxHeight: 240, overflowY: 'auto', marginBottom: 12,
            }}>
              {searching && (
                <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
                  Searching…
                </div>
              )}
              {!searching && results.length === 0 && (
                <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
                  No matching features
                </div>
              )}
              {results.map(f => {
                const already = selected.some(s => s.feature_id === f.feature_id);
                return (
                  <div key={f.id}
                    onClick={() => !already && addSelected(f)}
                    style={{
                      padding: '7px 12px', cursor: already ? 'default' : 'pointer',
                      fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
                      borderBottom: '1px solid var(--border-subtle)',
                      opacity: already ? 0.5 : 1,
                    }}
                    onMouseEnter={e => !already && (e.currentTarget.style.background = 'var(--surface-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {!already && <IconPlus />}
                    <code style={{ color: 'var(--info)', flexShrink: 0 }}>{f.feature_id}</code>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.major_feature}{f.sub_feature ? ` › ${f.sub_feature}` : ''}
                      {f.product_name ? ` — ${f.product_name}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Replace toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
            marginBottom: 14, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={replace}
              onChange={e => setReplace(e.target.checked)} />
            Replace — make this file’s feature set exactly the selected features
            (unlinks any others)
          </label>

          <button className="btn btn-primary" style={{ fontSize: 13 }}
            disabled={!canQuery || selected.length === 0 || saving}
            onClick={handleSave}>
            {saving ? 'Saving…'
              : replace ? `Replace mapping (${selected.length})`
              : `Add ${selected.length} feature${selected.length === 1 ? '' : 's'}`}
          </button>
          {!canQuery && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 10 }}>
              Enter a repository and file path first.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
